/** Phase 16.R11 / W3.BUG-4 — shared YouTube Data API category / privacy defaults. */

export const YOUTUBE_DEFAULT_CATEGORY_ID = '22'; // People & Blogs
export const YOUTUBE_DEFAULT_PRIVACY = 'public';

export const YOUTUBE_PRIVACY_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'unlisted', label: 'Unlisted' },
  { value: 'private', label: 'Private' },
];

export const YOUTUBE_CATEGORY_OPTIONS = [
  { value: '22', label: 'People & Blogs' },
  { value: '23', label: 'Comedy' },
  { value: '24', label: 'Entertainment' },
  { value: '25', label: 'News & Politics' },
  { value: '26', label: 'Howto & Style' },
  { value: '27', label: 'Education' },
  { value: '28', label: 'Science & Technology' },
  { value: '10', label: 'Music' },
  { value: '15', label: 'Pets & Animals' },
  { value: '17', label: 'Sports' },
  { value: '19', label: 'Travel & Events' },
  { value: '20', label: 'Gaming' },
];

export function defaultYoutubeSettings(overrides = {}) {
  return {
    title: '',
    description: '',
    tags: '',
    privacyStatus: YOUTUBE_DEFAULT_PRIVACY,
    categoryId: YOUTUBE_DEFAULT_CATEGORY_ID,
    playlistIds: [],
    ...overrides,
  };
}

/** Normalize API / asset youtube block into Gallery form values. */
export function youtubeSettingsFromAsset(platformSpecific) {
  const yt =
    platformSpecific && typeof platformSpecific === 'object'
      ? platformSpecific.youtube
      : null;
  if (!yt || typeof yt !== 'object') {
    return defaultYoutubeSettings();
  }
  const tags = Array.isArray(yt.tags)
    ? yt.tags.join(', ')
    : typeof yt.tags === 'string'
      ? yt.tags
      : '';
  const privacy = String(yt.privacyStatus || YOUTUBE_DEFAULT_PRIVACY).toLowerCase();
  const category = String(yt.categoryId || YOUTUBE_DEFAULT_CATEGORY_ID);
  const playlistIds = Array.isArray(yt.playlistIds)
    ? yt.playlistIds.map((id) => String(id)).filter(Boolean)
    : [];
  return {
    title: yt.title != null ? String(yt.title) : '',
    description: yt.description != null ? String(yt.description) : '',
    tags,
    privacyStatus: ['public', 'unlisted', 'private'].includes(privacy)
      ? privacy
      : YOUTUBE_DEFAULT_PRIVACY,
    categoryId: YOUTUBE_CATEGORY_OPTIONS.some((o) => o.value === category)
      ? category
      : YOUTUBE_DEFAULT_CATEGORY_ID,
    playlistIds,
  };
}

/** Build PATCH / promote payload for youtube metadata. */
export function youtubePayloadFromSettings(settings) {
  const s = settings || defaultYoutubeSettings();
  const tags = String(s.tags || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 20);
  const playlistIds = Array.isArray(s.playlistIds)
    ? s.playlistIds.map((id) => String(id)).filter(Boolean).slice(0, 50)
    : [];
  const payload = {
    title: String(s.title || '').trim().slice(0, 100),
    description: String(s.description || ''),
    tags,
    privacyStatus: s.privacyStatus || YOUTUBE_DEFAULT_PRIVACY,
    categoryId: s.categoryId || YOUTUBE_DEFAULT_CATEGORY_ID,
  };
  if (playlistIds.length) {
    payload.playlistIds = playlistIds;
  }
  return payload;
}

/**
 * W4.BUG-7: seed YouTube title/description/tags from an approved Gallery caption.
 * Preserves privacy / category / playlists from ``existing`` when provided.
 * Fields stay editable in the Gallery YouTube settings form.
 */
export function youtubeSettingsFromCaptionApproval({
  caption = '',
  hashtags = '',
  existing = null,
} = {}) {
  const text = String(caption || '');
  const firstLine =
    text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.length > 0) || text.trim();
  const title = firstLine.slice(0, 100);
  const tagList = Array.isArray(hashtags)
    ? hashtags
    : String(hashtags || '').split(',');
  const tags = tagList
    .map((t) => String(t || '').trim().replace(/^#/, ''))
    .filter(Boolean)
    .join(', ');
  const base = defaultYoutubeSettings(
    existing && typeof existing === 'object' ? existing : {},
  );
  return {
    ...base,
    title,
    description: text,
    tags,
  };
}
